/**
 * Disambiguate Actor
 *
 * Sends the patient's answer to a disambiguation field and receives a
 * PATIENT_MATCHED / NO_PATIENTS_MATCHED / MULTIPLE_PATIENTS_MATCHED response.
 *
 * The underlying server action is currently a mock. Swap the return value in
 * consumer-actions.ts to exercise each path locally while the real API is in flight.
 */

import { fromPromise } from "xstate";
import { z } from "zod";
import { trackNetworkRequest } from "../../lib/network-diagnostics";
import {
  type DisambiguateResponse,
  disambiguate,
} from "../../server-actions/consumer-actions";
import { fetchUserEmail } from "../../server-actions/user-actions";
import type { ActorMetadata, ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema — what this actor needs from machine context.
 */
const DisambiguateInputSchema = z.object({
  // Phone + DOB used in normal match mode; email used in email match mode
  phoneNumber: z.string().optional(),
  dob: z.string().optional(),
  orgId: z.number(),
  correlationId: z.string(),
  // Field-keyed optional disambiguation answer fields
  zip: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  address: z.string().optional(),
  email: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

type DisambiguateInput = z.infer<typeof DisambiguateInputSchema>;

/**
 * Success Schema — what this actor returns on success.
 */
const DisambiguateSuccessSchema = z.object({
  message: z.enum([
    "PATIENT_MATCHED",
    "NO_PATIENTS_MATCHED",
    "MULTIPLE_PATIENTS_MATCHED",
  ]),
  consumer: z
    .object({
      id: z.string(),
      userId: z.string(),
      firstname: z.string(),
      lastname: z.string(),
      dob: z.string(),
      sex: z.string().nullable(),
      streetAddress: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zip: z.string().nullable(),
      addressLine2: z.string().nullable(),
      ehrPatientId: z.string().nullable(),
      ehrEnterpriseId: z.string().nullable(),
      email: z.string().nullable(),
      insurances: z.array(z.any()).optional(),
      hipaaSignDate: z.string().nullable(),
      emergencyContactFirstName: z.string().nullable().optional(),
      emergencyContactLastName: z.string().nullable().optional(),
      emergencyContactPhone: z.string().nullable().optional(),
      emergencyContactRelationship: z.string().nullable().optional(),
      pcpFirstName: z.string().nullable().optional(),
      pcpLastName: z.string().nullable().optional(),
      pcpPhone: z.string().nullable().optional(),
      pcpAddress: z
        .object({
          streetAddress: z
            .union([
              z.string(),
              z.object({
                streetAddress: z.string().optional(),
                city: z.string().optional(),
                state: z.string().optional(),
                zip: z.string().optional(),
              }),
            ])
            .optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zip: z.string().optional(),
        })
        .nullable()
        .optional()
        .transform(
          (
            val,
          ):
            | {
                streetAddress?: string;
                city?: string;
                state?: string;
                zip?: string;
              }
            | null
            | undefined => {
            if (!val) return val;
            if (val.streetAddress && typeof val.streetAddress === "object") {
              const inner = val.streetAddress;
              const flat = {
                streetAddress: inner.streetAddress,
                city: inner.city,
                state: inner.state,
                zip: inner.zip,
              };
              return Object.values(flat).some((v) => v !== undefined)
                ? flat
                : null;
            }
            const flat = {
              streetAddress: val.streetAddress as string | undefined,
              city: val.city,
              state: val.state,
              zip: val.zip,
            };
            return Object.values(flat).some((v) => v !== undefined)
              ? flat
              : null;
          },
        ),
      pharmacyName: z.string().nullable().optional(),
      pharmacyPhone: z.string().nullable().optional(),
      pharmacyAddress: z
        .object({
          streetAddress: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zip: z.string().optional(),
        })
        .nullable()
        .optional(),
      referralSource: z.string().nullable().optional(),
    })
    .nullable(),
  userId: z.string().optional(),
  consumerId: z.string().optional(),
  correlationId: z.string().optional(),
  disambiguationField: z.string().optional(),
});

type DisambiguateSuccess = z.infer<typeof DisambiguateSuccessSchema>;

/**
 * Actor Implementation
 */
const disambiguateActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: DisambiguateInput;
  }): Promise<DisambiguateSuccess> => {
    const validated = DisambiguateInputSchema.safeParse(input);

    if (!validated.success) {
      throw new Error(
        `disambiguate validation failed: ${validated.error.message}`,
      );
    }

    const { orgId } = validated.data;

    // Strip the +1 country code prefix added by ContactInput's E.164 transform.
    // The match/disambiguate endpoint expects a 10-digit US number.
    // In email match mode phoneNumber is absent — skip normalisation.
    const request = {
      ...validated.data,
      ...(validated.data.phoneNumber && {
        phoneNumber: validated.data.phoneNumber.replace(/^\+1/, ""),
      }),
    };

    const tracker = trackNetworkRequest("disambiguate", { orgId });
    try {
      const response: DisambiguateResponse = await disambiguate(request);
      tracker.success();

      const consumer = response.data.consumer;

      // Fetch email from User record (not on Consumer).
      // On fetch failure, leave email off — verify mode will prompt for it.
      const emailResult = consumer?.userId
        ? await fetchUserEmail(consumer.userId)
        : null;
      const resolvedEmail =
        emailResult && emailResult.status === "ok" ? emailResult.email : null;
      const consumerWithEmail = consumer
        ? { ...consumer, ...(resolvedEmail ? { email: resolvedEmail } : {}) }
        : consumer;
      return {
        message: response.data.message,
        consumer: consumerWithEmail,
        userId: consumer?.userId ?? undefined,
        consumerId: consumer?.id ?? undefined,
        correlationId: response.data.correlationId,
        disambiguationField: response.data.disambiguationField,
      };
    } catch (error) {
      tracker.error(error);
      throw error;
    }
  },
);

/**
 * Disambiguate Actor with Metadata
 */
export const disambiguateActor: ActorWithMetadata = {
  impl: disambiguateActorImpl,
  metadata: {
    displayName: "disambiguate",
    description:
      "Sends a disambiguation field answer to confirm which patient is checking in",
    category: "auth",
    retryable: false,
    timeout: 10000,
    errorCodes: [],
    inputSchema: DisambiguateInputSchema,
    successSchema: DisambiguateSuccessSchema,
    errorSchema: z.object({}),
    onSuccess: (result: unknown) => {
      const r = result as DisambiguateSuccess;
      return {
        userId: r.userId,
        consumerId: r.consumerId,
        correlationId: r.correlationId,
        disambiguationField: r.disambiguationField,
      };
    },
    onError: (_error: unknown) => {
      return {};
    },
  } satisfies ActorMetadata,
};
